import { useState } from "react";
import { ChevronDown, ChevronUp, Clock, GripVertical, History, Repeat, User, Users } from "lucide-react";
import { isTarefaRecorrente } from "./recorrenciaUtils";
import { cn } from "@/utils/cn";
import { isTarefaCompartilhada, type TarefaDto, type TarefaStatus } from "@/services/tarefas";
import { QUICK_STATUS_BUTTONS, STATUS_CARD_THEME } from "./statusTheme";
import { setTarefaDragData } from "./tarefaDrag";

interface CardTarefaProps {
  tarefa: TarefaDto;
  currentUserId: string;
  busy?: boolean;
  draggable?: boolean;
  isDragging?: boolean;
  onEdit: (tarefa: TarefaDto) => void;
  onLeadtime: (tarefa: TarefaDto) => void;
  onStatusChange: (tarefa: TarefaDto, status: TarefaStatus) => void;
  onDragStart?: (tarefaId: string) => void;
  onDragEnd?: () => void;
}

function sharedUsersLabel(tarefa: TarefaDto, currentUserId: string): string[] {
  const isShared = isTarefaCompartilhada(tarefa, currentUserId);
  if (isShared) return [tarefa.criadorName || "Criador"];
  if (tarefa.usuarioNomes?.length) return tarefa.usuarioNomes;
  return [];
}

export function CardTarefa({
  tarefa,
  currentUserId,
  busy = false,
  draggable = false,
  isDragging = false,
  onEdit,
  onLeadtime,
  onStatusChange,
  onDragStart,
  onDragEnd,
}: CardTarefaProps) {
  const [expanded, setExpanded] = useState(false);
  const theme = STATUS_CARD_THEME[tarefa.status] ?? STATUS_CARD_THEME.pendente;
  const concluida = tarefa.status === "concluida";
  const sharedWith = sharedUsersLabel(tarefa, currentUserId);
  const hasShared = sharedWith.length > 0;
  const hasDescricao = tarefa.descricao.trim().length > 0;
  const isShared = isTarefaCompartilhada(tarefa, currentUserId);
  const recorrente = isTarefaRecorrente(tarefa);

  const handleStatusClick = (status: TarefaStatus) => {
    const next = tarefa.status === status ? "pendente" : status;
    onStatusChange(tarefa, next);
  };

  return (
    <div
      draggable={draggable && !busy}
      onDragStart={(e) => {
        if (!draggable) return;
        setTarefaDragData(e.dataTransfer, tarefa.id);
        onDragStart?.(tarefa.id);
      }}
      onDragEnd={() => onDragEnd?.()}
      className={cn(
        "w-full overflow-hidden rounded-md border transition-colors",
        "border-l-[3px]",
        theme.border,
        theme.left,
        theme.bg,
        draggable && "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-40 ring-2 ring-blue-300 ring-offset-1",
      )}
    >
      <div className="px-2.5 py-2">
        <div className="flex items-start gap-1">
          {draggable && (
            <div
              className="mt-0.5 flex-shrink-0 text-slate-300"
              title="Arraste para outro dia"
            >
              <GripVertical className="h-4 w-4" />
            </div>
          )}
          <button
            type="button"
            onClick={() => onEdit(tarefa)}
            className="min-w-0 flex-1 text-left focus:outline-none"
          >
            {tarefa.horario && (
              <div className="mb-0.5 flex items-center gap-1 text-[10px] font-medium text-slate-500">
                <Clock className="h-3 w-3 flex-shrink-0" />
                <span>
                  {tarefa.horario}
                  {tarefa.horarioFim && ` – ${tarefa.horarioFim}`}
                </span>
                {recorrente && <Repeat className="h-3 w-3 text-violet-500" aria-label="Recorrente" />}
              </div>
            )}
            {!tarefa.horario && recorrente && (
              <div className="mb-0.5 flex items-center gap-1 text-[10px] text-violet-500">
                <Repeat className="h-3 w-3" />
                <span>Recorrente</span>
              </div>
            )}
            <p
              className={cn(
                "text-xs font-medium leading-snug",
                theme.text,
                "whitespace-normal break-words",
                concluida && "line-through opacity-70",
              )}
            >
              {tarefa.titulo}
            </p>
          </button>

          <div className="flex flex-shrink-0 flex-col items-end gap-1">
            <div className="flex items-center gap-1">
              {QUICK_STATUS_BUTTONS.map(({ status, title }) => {
                const active = tarefa.status === status;
                const btnTheme = STATUS_CARD_THEME[status];
                return (
                  <button
                    key={status}
                    type="button"
                    title={active ? `Voltar para pendente` : title}
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusClick(status);
                    }}
                    className={cn(
                      "rounded-full p-0.5 transition-all disabled:opacity-50",
                      active && `ring-2 ring-offset-1 ${btnTheme.dotRing}`,
                    )}
                  >
                    <span
                      className={cn(
                        "block h-3.5 w-3.5 rounded-full",
                        btnTheme.dot,
                        !active && "opacity-40 hover:opacity-70",
                      )}
                    />
                  </button>
                );
              })}
            </div>
            {(hasDescricao || hasShared) && (
              <button
                type="button"
                title={expanded ? "Recolher" : "Expandir"}
                onClick={() => setExpanded((v) => !v)}
                className="rounded p-0.5 text-slate-400 hover:text-slate-600"
              >
                {expanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {hasShared && !expanded && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-black/5 px-2.5 py-1.5">
          <Users className="h-3 w-3 flex-shrink-0 text-slate-400" />
          <span className="text-[10px] text-slate-400">{isShared ? "De" : "Com"}</span>
          {sharedWith.map((nome) => (
            <span
              key={nome}
              className="inline-flex items-center gap-1 text-[10px] text-slate-600"
            >
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-slate-200 text-[8px] font-medium text-slate-600">
                {nome.charAt(0).toUpperCase()}
              </span>
              {nome}
            </span>
          ))}
        </div>
      )}

      {expanded && (
        <div className="border-t border-black/5 px-2.5 py-2">
          {hasDescricao && (
            <p className="mb-1.5 whitespace-normal break-words text-[10px] leading-relaxed text-slate-500">
              {tarefa.descricao}
            </p>
          )}
          {hasShared && (
            <div className="flex items-start gap-1.5">
              <User className="mt-0.5 h-3 w-3 flex-shrink-0 text-slate-400" />
              <div>
                <p className="text-[10px] text-slate-400">
                  {isShared ? "Criado por" : "Compartilhado com"}
                </p>
                <ul className="mt-0.5 space-y-0.5">
                  {sharedWith.map((nome) => (
                    <li key={nome} className="text-[10px] text-slate-600">
                      {nome}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <div className="mt-1.5 flex justify-end">
            <button
              type="button"
              title="Ver leadtime"
              disabled={busy}
              onClick={() => onLeadtime(tarefa)}
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-brand-700"
            >
              <History className="h-3 w-3" />
              Leadtime
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
